import { jsxs, jsx } from "react/jsx-runtime";
import API from "@zillow/mortgage-api-client";
import _debounce from "lodash/debounce";
import React, { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { setServerUrl, createMax } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
import withDimensions from "../PaymentDonutChart/withDimensions.esm.js";
import MortgageRateHistoryTable from "./MortgageRateHistoryTable.esm.js";
import { useSyncFetchHistory } from "./MortgageRateTrends.utils.esm.js";
import { PROGRAM_COLOR_CODE } from "./constants.esm.js";
const getSize = ({
  height,
  width,
  containerWidth
}) => {
  const h = Math.round(height);
  const w = Math.round(width);
  if (h && w) {
    return {
      chartHeight: h,
      chartWidth: w
    };
  } else if (h) {
    return {
      chartHeight: h,
      chartWidth: Math.round(containerWidth)
    };
  } else if (w) {
    return {
      chartHeight: Math.round(width / 3 * 2),
      chartWidth: w
    };
  } else {
    return {
      chartHeight: Math.round(containerWidth / 3 * 2),
      chartWidth: Math.round(containerWidth)
    };
  }
};
const MortgageRateTrendsChart = ({
  hideGridLines,
  height,
  width,
  // Only used by Dimensions. Use height and width to specify your own values.
  containerWidth,
  flavor
}) => {
  const [dimensions, setDimensions] = useState(() => getSize({
    height,
    width,
    containerWidth
  }));
  useEffect(() => {
    setServerUrl();
    createMax();
  }, []);
  useEffect(() => {
    const updateSize = _debounce(() => {
      setDimensions(getSize({
        height,
        width,
        containerWidth
      }));
    }, 150);
    updateSize();
    return () => updateSize.cancel();
  }, [containerWidth, height, width]);
  const form = useFormContext();
  const results = useSyncFetchHistory(form, flavor);
  const chartUrl = useMemo(() => {
    var _a;
    const {
      chartWidth,
      chartHeight
    } = dimensions;
    let url = null;
    const lines = results == null ? void 0 : results.checkedLoanProgramFields.map((program) => {
      var _a2, _b;
      return {
        query: {
          stateAbbreviation: results == null ? void 0 : results.location,
          program,
          loanToValueBucket: results == null ? void 0 : results.loanToValueBucket,
          creditScoreBucket: results == null ? void 0 : results.creditScoreBucket,
          refinance: ((_a2 = results == null ? void 0 : results.currentFieldValues) == null ? void 0 : _a2.loanPurpose) == "Refinance" ? true : false
        },
        color: (_b = PROGRAM_COLOR_CODE[program]) == null ? void 0 : _b.replace("#", "")
      };
    });
    if (chartWidth && chartHeight) {
      try {
        url = API.MAX.url({
          action: "rate-chart",
          input: {
            width: Math.max(200, chartWidth),
            height: Math.max(200, chartHeight),
            tickLabelColor: "333333",
            tickLabelFontSize: 8.5,
            xAxisTickCount: 3,
            yAxisTickCount: 4,
            hideGridLines,
            durationDays: (_a = results == null ? void 0 : results.currentFieldValues) == null ? void 0 : _a.timeSpan,
            lines
          }
        });
        return url;
      } catch (e) {
        return null;
      }
    }
  }, [results, dimensions, hideGridLines]);
  const chartStyle = useMemo(() => {
    const {
      chartHeight,
      chartWidth
    } = dimensions;
    const h = Math.round(height);
    const w = Math.round(width);
    const results2 = {
      height: `${chartHeight}px`,
      backgroundRepeat: "no-repeat",
      width: "",
      backgroundSize: "",
      backgroundImage: ""
    };
    if (h && w) {
      results2.width = `${chartWidth}px`;
    } else if (h) {
      results2.backgroundSize = `100% ${chartHeight}px`;
    } else {
      results2.backgroundSize = "contain";
    }
    if (chartUrl) {
      results2.backgroundImage = `url("${chartUrl}")`;
    }
    return results2;
  }, [chartUrl, dimensions, height, width]);
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    /* @__PURE__ */ jsx("div", { style: chartStyle, id: "rates-explorer-chart", role: "img", "aria-label": "Historical mortgage rates plotted as line graph.", "aria-describedby": "rates-explorer-chart-description" }),
    /* @__PURE__ */ jsx(MortgageRateHistoryTable, { id: "rates-explorer-chart-description", flavor })
  ] });
};
const MortgageRateTrendsChart$1 = withDimensions(MortgageRateTrendsChart, {
  defaultWidth: 980,
  defaultHeight: null
});
export {
  MortgageRateTrendsChart$1 as default
};
